const STORAGE_KEY_PREFIX = 'raund-campaign-colors-'

/** `#RRGGBB` 形式のみ許可する正規表現 */
const HEX_COLOR_REGEX = /^#[0-9a-fA-F]{6}$/

export type CampaignColorCache = {
  mainColor: string
  subColor: string
  /**
   * メインカラー / サブカラーを背景に敷いたときの文字色（#3529）。
   *
   * `_app.tsx` が `resolveOnColor()` で算出して保存し、`_document.tsx` の
   * FOUC 防止スクリプトはこれを読むだけにする（輝度計算をインラインで複製しないため）。
   * 旧バージョンが書いたキャッシュには存在しないため optional。
   */
  onMainColor?: string
  onSubColor?: string
}

/** 値が `#RRGGBB` 形式の有効な HEX カラーかどうかを検証する */
function isValidHexColor(value: unknown): value is string {
  return typeof value === 'string' && HEX_COLOR_REGEX.test(value.trim())
}

/** キャンペーンカラーをローカルストレージに保存する */
export function saveCampaignColors(
  campaignSlug: string,
  mainColor: string,
  subColor: string,
  onMainColor?: string,
  onSubColor?: string,
): void {
  if (typeof window === 'undefined') return
  // 不正値を永続化しないよう、保存前に HEX バリデーションを行う
  if (!isValidHexColor(mainColor) || !isValidHexColor(subColor)) return
  try {
    const key = `${STORAGE_KEY_PREFIX}${campaignSlug}`
    const value: CampaignColorCache = {
      mainColor: mainColor.trim(),
      subColor: subColor.trim(),
      // 文字色は不正値なら保存しない（_document.tsx 側でも再検証する）
      ...(isValidHexColor(onMainColor) ? { onMainColor: onMainColor.trim() } : {}),
      ...(isValidHexColor(onSubColor) ? { onSubColor: onSubColor.trim() } : {}),
    }
    window.localStorage.setItem(key, JSON.stringify(value))
  } catch {
    // localStorage が使用不可（Safari プライベートモード等）でも無視
  }
}

/** キャンペーンカラーをローカルストレージから読み込む */
export function loadCampaignColors(campaignSlug: string): CampaignColorCache | null {
  if (typeof window === 'undefined') return null
  try {
    const key = `${STORAGE_KEY_PREFIX}${campaignSlug}`
    const raw = window.localStorage.getItem(key)
    if (!raw) return null
    const parsed: unknown = JSON.parse(raw)
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      'mainColor' in parsed &&
      'subColor' in parsed &&
      isValidHexColor((parsed as CampaignColorCache).mainColor) &&
      isValidHexColor((parsed as CampaignColorCache).subColor)
    ) {
      const cache = parsed as CampaignColorCache
      return {
        mainColor: cache.mainColor.trim(),
        subColor: cache.subColor.trim(),
        ...(isValidHexColor(cache.onMainColor) ? { onMainColor: cache.onMainColor.trim() } : {}),
        ...(isValidHexColor(cache.onSubColor) ? { onSubColor: cache.onSubColor.trim() } : {}),
      }
    }
    return null
  } catch {
    return null
  }
}
